import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { not, or } from 'ember-truth-helpers';
import { PhoneNumberInput } from 'frontend-utils';
import { LANGUAGES_LIST } from 'frontend-utils/utils/constants/constant-values';
import isValidEmailAddress from 'frontend-utils/utils/is-valid-email-address';
import {
  formatPhoneNumber,
  parsePhoneString,
} from 'frontend-utils/utils/phone-number-utils';
import hasUnifiedSettingsActiveFeature from 'lumen/helpers/has-unified-settings-active-feature';
import type { SubscriptionProfile } from 'lumen/models';
import { GENERAL_PROPERTY_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';

const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;

// UNIFIED SETTINGS TODO: [INDU-1352] Discuss with the team to move this code to its own file,
//                        and terminate duplication with apps/lumen/app/components/settings/personal/forms/form-user-edit.gts
type Language = {
  id: string;
  label: string;
};

const DEFAULT_LANGUAGE: Language = {
  id: '',
  label: '',
};

function getUserLanguage(
  locale: string | undefined,
  languagesList: Language[],
): Language {
  return languagesList.find((lang) => lang.id === locale) ?? DEFAULT_LANGUAGE;
}

interface SettingsMyHotelCardBusinessProfileSignature {
  Args: {
    isLoading: boolean;
    profile: SubscriptionProfile | null | undefined;
  };
}

export default class SettingsMyHotelCardBusinessProfile extends Component<SettingsMyHotelCardBusinessProfileSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: IntlService;
  @service declare toast: Services['toast'];

  @tracked hasAttemptedSave = false;
  @tracked isPhoneNumberValid = true;

  // This constant stores the properties of the model that should be monitored in this card for isDirty state.
  // Scoped rollback — rollbackAttributes() would also revert address/lat/lng owned by CardAddress
  private static readonly OWNED_ATTRS = [
    'email',
    'language',
    'telnr',
    'brandUrl',
  ] as const;

  saveTask = task({ drop: true }, async (): Promise<void> => {
    this.hasAttemptedSave = true;

    if (!this.hasChanges) {
      return;
    }

    if (this.hasValidationErrors) {
      return;
    }

    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.email = profile.email.trim();
    profile.telnr = profile.telnr.trim();
    profile.brandUrl = profile.brandUrl.trim();

    try {
      await profile.saveForSubscription(profile.id!);
      this.toast.success(
        this.intl.t('settings.my_hotel.hotel_details.save_success'),
      );
      this.hasAttemptedSave = false;
    } catch {
      await this.alerting.showErrorAlert();
    }
  });

  // UNIFIED SETTINGS TODO: [INDU-1352] Check the need of this getter: Maybe not required
  //                        duplicated from apps/lumen/app/components/settings/personal/forms/form-user-edit.gts
  get availableLanguages(): Language[] {
    return LANGUAGES_LIST;
  }

  get emailErrorMessage(): string | undefined {
    if (!this.isEmailValid) {
      return this.intl.t('settings.my_hotel.contact_details.email_invalid');
    }

    return undefined;
  }

  get hasChanges(): boolean {
    const { profile } = this.args;
    if (!profile?.hasDirtyAttributes) {
      return false;
    }
    const changed = profile.changedAttributes();
    return SettingsMyHotelCardBusinessProfile.OWNED_ATTRS.some(
      (attr) => attr in changed,
    );
  }

  get hasValidationErrors(): boolean {
    return (
      !this.isEmailValid || !this.isWebsiteValid || !this.isPhoneNumberValid
    );
  }

  get isEmailValid(): boolean {
    const email = this.args.profile?.email ?? '';

    return email === '' || isValidEmailAddress(email);
  }

  get isWebsiteValid(): boolean {
    const brandUrl = this.args.profile?.brandUrl ?? '';

    return (
      brandUrl === '' || (brandUrl.length <= 255 && URL_REGEX.test(brandUrl))
    );
  }

  @cached
  get parsedPhone(): {
    phoneCountry: { code: string; phoneCode: string };
    phoneNumber: string;
  } {
    return parsePhoneString(this.args.profile?.telnr ?? '');
  }

  get phoneCountry(): { code: string; phoneCode: string } {
    return this.parsedPhone.phoneCountry;
  }

  get phoneNumber(): string {
    return this.parsedPhone.phoneNumber;
  }

  get selectedLanguage(): Language {
    return getUserLanguage(
      this.args.profile?.language,
      this.availableLanguages,
    );
  }

  get websiteErrorMessage(): string | undefined {
    if (!this.isWebsiteValid) {
      return this.intl.t('settings.my_hotel.contact_details.website_invalid');
    }

    return undefined;
  }

  @action async cancel(): Promise<void> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.keep_editing'),
        },
        confirm: {
          text: this.intl.t('general.discard_changes'),
        },
      },
    });

    if (confirmed) {
      const { profile } = this.args;
      if (profile) {
        const changed = profile.changedAttributes();
        for (const attr of SettingsMyHotelCardBusinessProfile.OWNED_ATTRS) {
          if (attr in changed) {
            (profile as unknown as Record<string, unknown>)[attr] =
              changed[attr]![0];
          }
        }
      }

      this.isPhoneNumberValid = true;
      this.hasAttemptedSave = false;
    }
  }

  @action setField(field: 'brandUrl' | 'email' | 'telnr', event: Event): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile[field] = (event.target as HTMLInputElement).value;
  }

  @action updateCountryCode(countryCode: {
    code: string;
    phoneCode: string;
  }): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.telnr = formatPhoneNumber(countryCode.phoneCode, this.phoneNumber);
  }

  @action updateLanguage(language: Language): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.language = language.id;
  }

  @action updatePhoneNumber(phoneNumber: string): void {
    const { profile } = this.args;
    if (!profile) {
      return;
    }

    profile.telnr = formatPhoneNumber(this.phoneCountry.phoneCode, phoneNumber);
  }

  @action updatePhoneNumberValidity(isValid: boolean): void {
    this.isPhoneNumberValid = isValid;
  }

  <template>
    <PrismCard data-test-id="card-business-profile">
      <:header as |Header|>
        <Header.title>
          {{t "settings.my_hotel.hotel_details.business_profile"}}
        </Header.title>
      </:header>

      <:content>
        <div class="grid grid-cols-3 gap-400 pt-400">
          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_NAME
            )
          }}
            <div class="col-span-3 flex">
              <PrismInput
                @disabled={{true}}
                @label={{t "settings.my_hotel.hotel_details.hotel_name"}}
                @removePadding={{true}}
                @value={{@profile.hotelName}}
                data-test-id="input-hotel-name"
              />
            </div>
          {{/if}}

          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_EMAIL
            )
          }}
            <div class="col-span-2 flex self-start">
              <PrismInput
                @errorMessage={{this.emailErrorMessage}}
                @hasErrors={{not this.isEmailValid}}
                @isTouched={{this.hasAttemptedSave}}
                @label={{t "settings.my_hotel.contact_details.email"}}
                @onInput={{fn this.setField "email"}}
                @removePadding={{true}}
                @type="email"
                @value={{@profile.email}}
                data-test-id="input-email"
              />
            </div>
          {{/if}}

          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_PHONE
            )
          }}
            <div class="flex self-start" data-test-id="input-phone">
              <PhoneNumberInput
                @fillWidth={{true}}
                @labelText={{t "settings.my_hotel.contact_details.phone"}}
                @onCountryCodeChange={{this.updateCountryCode}}
                @onPhoneNumberChange={{this.updatePhoneNumber}}
                @phoneNumber={{this.phoneNumber}}
                @selectedCountryCode={{this.phoneCountry}}
                @updateIsPhoneNumberValid={{this.updatePhoneNumberValidity}}
              />
            </div>
          {{/if}}

          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_WEBSITE
            )
          }}
            <div class="col-span-2 flex">
              <PrismInput
                @errorMessage={{this.websiteErrorMessage}}
                @hasErrors={{not this.isWebsiteValid}}
                @isTouched={{this.hasAttemptedSave}}
                @label={{t "settings.my_hotel.contact_details.website"}}
                @onInput={{fn this.setField "brandUrl"}}
                @removePadding={{true}}
                @value={{@profile.brandUrl}}
                data-test-id="input-website"
              />
            </div>
          {{/if}}

          {{#if
            (hasUnifiedSettingsActiveFeature
              GENERAL_PROPERTY_FEATURES.HOTEL_LANGUAGE
            )
          }}
            <div class="grid self-start">
              <label class="input-label text-200--regular">
                {{t "settings.my_hotel.hotel_details.language"}}
              </label>
              <PrismSelectInput
                @isSecondary={{true}}
                @onChange={{this.updateLanguage}}
                @options={{this.availableLanguages}}
                @value={{this.selectedLanguage}}
                data-test-id="input-language"
              />
            </div>
          {{/if}}
        </div>
      </:content>

      <:footer>
        <div class="flex justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{or (not this.hasChanges) this.saveTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{this.cancel}}
            data-test-id="button-cancel-business-profile"
          />
          <PrismButton
            @isDisabled={{not this.hasChanges}}
            @isLoading={{or @isLoading this.saveTask.isRunning}}
            @label={{t "general.save"}}
            @onClick={{this.saveTask.perform}}
            data-test-id="button-save-business-profile"
          />
        </div>
      </:footer>
    </PrismCard>
  </template>
}
